import { Globe, Inbox, Map } from '@/components/icons';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Link, useNavigate } from '@tanstack/react-router';
import { useUnreadNotificationCount } from '../../lib/convex-hooks';
import { useCurrentCrewMember } from '../../hooks/useCurrentCrewMember';
import { cn } from '@/lib/utils';
import type { FlightMapMode } from '@/lib/native/flight-map';

interface TopBarHomeProps {
  userName: string;
  userInitials: string;
  avatarColor?: string;
  onNotificationsClick?: VoidFunction;
  transparent?: boolean;
  /** Current map mode. When provided alongside `onToggleMapMode`, a map toggle button is shown. */
  mapMode?: FlightMapMode;
  onToggleMapMode?: () => void;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function TopBarHome({
  userName,
  userInitials,
  avatarColor,
  transparent,
  mapMode,
  onToggleMapMode,
}: TopBarHomeProps) {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentCrewMember();
  const unreadCount = useUnreadNotificationCount(currentUser?.id);

  return (
    <>
      {/* Spacer to reserve space in document flow — must match fixed header height */}
      <div className="shrink-0 safe-top">
        <div className="h-16" />
      </div>
      {/* Fixed header */}
      <div className={cn("fixed top-0 left-0 right-0 z-50 safe-top", transparent ? "bg-transparent" : "bg-surface-brand-L0")}>
        <div className="content-frame px-gutter py-4 flex items-center justify-between">
        {/* Left: Greeting */}
        <div className="flex flex-col">
          <span className={cn("text-base font-semibold leading-6 tracking-[-0.5px]", transparent ? "text-white" : "text-content-primary-bold")}>
            {getGreeting()}
          </span>
          <span className={cn("text-[22px] font-bold leading-7 tracking-[-1px]", transparent ? "text-white" : "text-content-primary-bold")}>
            {userName}
          </span>
        </div>

        {/* Right: Avatar + Inbox + Map toggle */}
        <div className="flex items-center gap-2">
          <Link to={'/profile'}>
            <Avatar className="size-8">
              <AvatarFallback className="text-sm font-semibold text-white" style={avatarColor ? { backgroundColor: avatarColor } : undefined}>{userInitials}</AvatarFallback>
            </Avatar>
          </Link>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="relative size-8 border-none bg-black/50 backdrop-blur-md active:bg-black/60"
            onClick={() => navigate({ to: '/inbox' })}
          >
            <Inbox className="w-4 h-4 text-white" />
            {unreadCount !== undefined && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold text-white bg-content-negative-bold rounded-full">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
          {onToggleMapMode && (
            <Button
              variant="secondary"
              size="icon"
              roundness="round"
              aria-label={mapMode === 'globe' ? 'Switch to flat map' : 'Switch to globe'}
              className="size-8 border-none bg-black/50 backdrop-blur-md active:bg-black/60"
              onClick={onToggleMapMode}
            >
              {mapMode === 'globe' ? (
                <Map className="w-4 h-4 text-white" />
              ) : (
                <Globe className="w-4 h-4 text-white" />
              )}
            </Button>
          )}
        </div>
        </div>
      </div>
    </>
  );
}
